import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { PublicLayout, CleanLayout } from './layouts';
import DashboardLayout from './components/DashboardLayout';
import AdminLayout from './components/AdminLayout';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute, GuestRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/common';

import LandingPage from './pages/LandingPage';
import Overseas from './pages/Overseas';
import ScratchCards from './pages/ScratchCards';
import DigitalSchool from './pages/DigitalSchool';
import ExamPrep from './pages/ExamPrep';
import UKStudyPage from './pages/overseas/UK';
import UKPathfinder from './pages/overseas/UK/Pathfinder';
import FinancialAnalysis from './pages/overseas/UK/FinancialAnalysis';
import UKReadinessAssessment from './pages/overseas/UK/ReadinessAssessment';

import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import ResendVerification from './pages/ResendVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import ApplicationProgress from './pages/ApplicationProgress';
import DocumentSubmission from './pages/DocumentSubmission';
import Profile from './pages/Profile';
import Settings from './pages/Settings';

// New role-based dashboards
import DashboardRouter from './pages/dashboards/DashboardRouter';
import StudentDashboard from './pages/dashboards/StudentDashboard';
import ProfessionalDashboard from './pages/dashboards/ProfessionalDashboard';
import EducatorDashboard from './pages/dashboards/EducatorDashboard';
import ServicePortal from './pages/dashboards/ServicePortal';

// Service-specific routes
import OverseasServiceRoutes from './pages/services/overseas/OverseasServiceRoutes';
import CareerServiceRoutes from './pages/services/career/CareerServiceRoutes';
import SkillsServiceRoutes from './pages/services/skills/SkillsServiceRoutes';
import TestPrepServiceRoutes from './pages/services/test-prep/TestPrepServiceRoutes';
import ScholarshipsServiceRoutes from './pages/services/scholarships/ScholarshipsServiceRoutes';
import VisaServiceRoutes from './pages/services/visa/VisaServiceRoutes';
import AccommodationServiceRoutes from './pages/services/accommodation/AccommodationServiceRoutes';
import PreDepartureServiceRoutes from './pages/services/pre-departure/PreDepartureServiceRoutes';
import PostArrivalServiceRoutes from './pages/services/post-arrival/PostArrivalServiceRoutes';
import AlumniServiceRoutes from './pages/services/alumni/AlumniServiceRoutes';

// Educator pages
import StudentManagement from './pages/educator/StudentManagement';
import StudentDetail from './pages/educator/StudentDetail';
import ContentLibrary from './pages/educator/ContentLibrary';
import ContentEditor from './pages/educator/ContentEditor';
import CourseList from './pages/educator/CourseList';
import CourseBuilder from './pages/educator/CourseBuilder';
import CourseAnalytics from './pages/educator/CourseAnalytics';

// Course pages
import CourseCatalog from './pages/courses/CourseCatalog';
import CourseDetail from './pages/courses/CourseDetail';
import QuizTaking from './pages/courses/QuizTaking';
import QuizBuilder from './pages/educator/QuizBuilder';
import AssignmentBuilder from './pages/educator/AssignmentBuilder';
import AssignmentGrading from './pages/educator/AssignmentGrading';
import AssignmentView from './pages/courses/AssignmentView';
import CourseLearning from './pages/courses/CourseLearning';
import MyCourses from './pages/courses/MyCourses';
import LearningProgress from './pages/LearningProgress';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import Applications from './pages/admin/Applications';
import ApplicationDetail from './pages/admin/ApplicationDetail';
import Documents from './pages/admin/Documents';

// Admin User Management pages
import UsersList from './pages/admin/users/UsersList';
import UserDetail from './pages/admin/users/UserDetail';
import UserCreate from './pages/admin/users/UserCreate';

// Admin Reports pages
import ReportsOverview from './pages/admin/reports/ReportsOverview';
import ServiceAnalytics from './pages/admin/reports/ServiceAnalytics';
import UserAnalytics from './pages/admin/reports/UserAnalytics';
import DataExport from './pages/admin/reports/DataExport';

// Admin Communications pages
import BulkNotifications from './pages/admin/operations/BulkNotifications';
import Announcements from './pages/admin/communications/Announcements';

// Admin Operations pages
import BatchUpdates from './pages/admin/operations/BatchUpdates';

// Admin Settings pages
import SystemConfig from './pages/admin/settings/SystemConfig';
import StatusesConfig from './pages/admin/settings/StatusesConfig';
import DocumentTypesConfig from './pages/admin/settings/DocumentTypesConfig';
import EmailTemplatesConfig from './pages/admin/settings/EmailTemplatesConfig';
import FeatureManagement from './pages/admin/settings/FeatureManagement';

// Admin Scratch Cards pages
import ScratchCardsManagement from './pages/admin/scratch-cards/ScratchCardsManagement';
import ProductForm from './pages/admin/scratch-cards/ProductForm';

// User pages
import PurchasedCards from './pages/user/PurchasedCards';

// Checkout pages
import ScratchCardCheckout from './pages/checkout/ScratchCardCheckout';

// Main App Content Component
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Map URL paths to page IDs for navigation active tracking
  const getPageFromPath = (pathname) => {
    const pathMap = {
      '/': 'home',
      '/digital-school': 'digital-school',
      '/overseas': 'overseas',
      '/exam-prep': 'exam-prep',
      '/scratch-cards': 'scratch-cards',
      '/signup': 'signup',
      '/login': 'login',
      '/dashboard': 'dashboard'
    };
    return pathMap[pathname] || 'home';
  };

  const currentPage = getPageFromPath(location.pathname);

  const handleNavigation = (page) => {
    const pageRoutes = {
      'home': '/',
      'digital-school': '/digital-school',
      'overseas': '/overseas',
      'exam-prep': '/exam-prep',
      'scratch-cards': '/scratch-cards',
      'signup': '/signup',
      'login': '/login',
      'dashboard': '/dashboard'
    };

    const route = pageRoutes[page] || '/';
    navigate(route);
  };

  return (
    <Routes>
      {/* Public pages with top navigation */}
      <Route 
        path="/" 
        element={
          <PublicLayout onNavigate={handleNavigation} currentPage={currentPage} theme="light">
            <LandingPage />
          </PublicLayout>
        } 
      />
      
      <Route
        path="/overseas"
        element={
          <PublicLayout onNavigate={handleNavigation} currentPage={currentPage} theme="light">
            <Overseas />
          </PublicLayout>
        }
      />

      <Route
        path="/overseas/uk" 
        element={
          <PublicLayout onNavigate={handleNavigation} currentPage={currentPage} theme="dark">
            <UKStudyPage />
          </PublicLayout>
        } 
      />

      <Route
        path="/overseas/uk/pathfinder"
        element={
          <PublicLayout onNavigate={handleNavigation} currentPage={currentPage} theme="light">
            <UKPathfinder />
          </PublicLayout>
        }
      />

      <Route
        path="/overseas/uk/financial-analysis"
        element={
          <PublicLayout onNavigate={handleNavigation} currentPage={currentPage} theme="light">
            <FinancialAnalysis />
          </PublicLayout>
        }
      />

      <Route
        path="/overseas/uk/readiness-assessment"
        element={
          <PublicLayout onNavigate={handleNavigation} currentPage={currentPage} theme="light">
            <UKReadinessAssessment />
          </PublicLayout>
        }
      />

      <Route
        path="/exam-prep"
        element={
          <PublicLayout onNavigate={handleNavigation} currentPage={currentPage} theme="light">
            <ExamPrep />
          </PublicLayout>
        }
      />
      
      <Route
        path="/scratch-cards"
        element={
          <PublicLayout onNavigate={handleNavigation} currentPage={currentPage} theme="light">
            <ScratchCards />
          </PublicLayout>
        }
      />

      <Route
        path="/digital-school"
        element={
          <PublicLayout onNavigate={handleNavigation} currentPage={currentPage} theme="light">
            <DigitalSchool />
          </PublicLayout>
        }
      />

      {/* Legacy route - redirect to digital school */}
      <Route
        path="/remote-teaching"
        element={
          <PublicLayout onNavigate={handleNavigation} currentPage={currentPage} theme="light">
            <DigitalSchool />
          </PublicLayout>
        }
      />

      {/* Guest-only auth paths (redirects to dashboard if logged in) */}
      <Route element={<GuestRoute />}>
        <Route
          path="/login"
          element={
            <CleanLayout>
              <Login />
            </CleanLayout>
          }
        />

        <Route
          path="/signup"
          element={
            <CleanLayout>
              <Signup />
            </CleanLayout>
          }
        />
      </Route>

      {/* Email verification and password reset pages (public) */}
      <Route
        path="/verify-email"
        element={
          <CleanLayout>
            <VerifyEmail />
          </CleanLayout>
        }
      />

      <Route
        path="/resend-verification"
        element={
          <CleanLayout>
            <ResendVerification />
          </CleanLayout>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <CleanLayout>
            <ForgotPassword />
          </CleanLayout>
        }
      />

      <Route
        path="/reset-password"
        element={
          <CleanLayout>
            <ResetPassword />
          </CleanLayout>
        }
      />

      {/* Private authenticated paths (redirects to login if guest) */}
      <Route element={<ProtectedRoute />}>
        {/* Dashboard Router - redirects to role-specific dashboard */}
        <Route
          path="/dashboard"
          element={<DashboardRouter />}
        />

        {/* Role-specific dashboards */}
        <Route
          path="/dashboard/student"
          element={
            <ProtectedRoute requiredRole="STUDENT">
              <DashboardLayout title="Student Dashboard">
                <StudentDashboard />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/professional"
          element={
            <ProtectedRoute requiredRole="PROFESSIONAL">
              <DashboardLayout title="Professional Dashboard">
                <ProfessionalDashboard />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/educator"
          element={
            <ProtectedRoute requiredRole="EDUCATOR">
              <DashboardLayout title="Educator Dashboard">
                <EducatorDashboard />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Legacy Dashboard (for backward compatibility) */}
        <Route
          path="/dashboard/legacy"
          element={<Dashboard />}
        />

        {/* Service Portal */}
        <Route
          path="/services"
          element={
            <DashboardLayout title="All Services">
              <ServicePortal />
            </DashboardLayout>
          }
        />

        {/* Overseas Service Routes */}
        <Route
          path="/services/overseas/*"
          element={
            <DashboardLayout title="Overseas Education">
              <OverseasServiceRoutes />
            </DashboardLayout>
          }
        />

        {/* Career Counseling Service Routes */}
        <Route
          path="/services/career/*"
          element={
            <DashboardLayout title="Career Counseling">
              <CareerServiceRoutes />
            </DashboardLayout>
          }
        />

        {/* Skill Development Service Routes */}
        <Route
          path="/services/skills/*"
          element={
            <DashboardLayout title="Skill Development">
              <SkillsServiceRoutes />
            </DashboardLayout>
          }
        />

        {/* Test Preparation Service Routes */}
        <Route
          path="/services/test-prep/*"
          element={
            <DashboardLayout title="Test Preparation">
              <TestPrepServiceRoutes />
            </DashboardLayout>
          }
        />

        {/* Scholarship Finder Service Routes */}
        <Route
          path="/services/scholarships/*"
          element={
            <DashboardLayout title="Scholarship Finder">
              <ScholarshipsServiceRoutes />
            </DashboardLayout>
          }
        />

        {/* Visa Assistance Service Routes */}
        <Route
          path="/services/visa/*"
          element={
            <DashboardLayout title="Visa Assistance">
              <VisaServiceRoutes />
            </DashboardLayout>
          }
        />

        {/* Accommodation Service Routes */}
        <Route
          path="/services/accommodation/*"
          element={
            <DashboardLayout title="Accommodation">
              <AccommodationServiceRoutes />
            </DashboardLayout>
          }
        />

        {/* Pre-Departure Service Routes */}
        <Route
          path="/services/pre-departure/*"
          element={
            <DashboardLayout title="Pre-Departure">
              <PreDepartureServiceRoutes />
            </DashboardLayout>
          }
        />

        {/* Post-Arrival Service Routes */}
        <Route
          path="/services/post-arrival/*"
          element={
            <DashboardLayout title="Post-Arrival">
              <PostArrivalServiceRoutes />
            </DashboardLayout>
          }
        />

        {/* Alumni Network Service Routes */}
        <Route
          path="/services/alumni/*"
          element={
            <DashboardLayout title="Alumni Network">
              <AlumniServiceRoutes />
            </DashboardLayout>
          }
        />

        {/* Educator Routes */}
        <Route
          path="/educator/students"
          element={
            <ProtectedRoute requiredRole="EDUCATOR">
              <DashboardLayout title="Student Management">
                <StudentManagement />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/educator/students/:id"
          element={
            <ProtectedRoute requiredRole="EDUCATOR">
              <DashboardLayout title="Student Detail">
                <StudentDetail />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/educator/content"
          element={
            <ProtectedRoute requiredRole="EDUCATOR">
              <DashboardLayout title="Content Library">
                <ContentLibrary />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/educator/content/new"
          element={
            <ProtectedRoute requiredRole="EDUCATOR">
              <DashboardLayout title="Create Content">
                <ContentEditor />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/educator/content/:id/edit"
          element={
            <ProtectedRoute requiredRole="EDUCATOR">
              <DashboardLayout title="Edit Content">
                <ContentEditor />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Educator Course Routes */}
        <Route
          path="/educator/courses"
          element={
            <ProtectedRoute requiredRole="EDUCATOR">
              <DashboardLayout title="My Courses">
                <CourseList />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/educator/courses/create"
          element={
            <ProtectedRoute requiredRole="EDUCATOR">
              <DashboardLayout title="Create Course">
                <CourseBuilder />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/educator/courses/:id/edit"
          element={
            <ProtectedRoute requiredRole="EDUCATOR">
              <DashboardLayout title="Edit Course">
                <CourseBuilder />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/educator/courses/:id/analytics"
          element={
            <ProtectedRoute requiredRole="EDUCATOR">
              <DashboardLayout title="Course Analytics">
                <CourseAnalytics />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Course Routes - Available to Students */}
        <Route
          path="/courses/catalog"
          element={
            <DashboardLayout title="Course Catalog">
              <CourseCatalog />
            </DashboardLayout>
          }
        />

        <Route
          path="/courses/:id"
          element={
            <DashboardLayout title="Course Details">
              <CourseDetail />
            </DashboardLayout>
          }
        />

        {/* Quiz Routes */}
        <Route
          path="/quiz/:quizId/take"
          element={
            <ProtectedRoute>
              <QuizTaking />
            </ProtectedRoute>
          }
        />

        {/* Assignment Routes */}
        <Route
          path="/assignment/:assignmentId"
          element={
            <ProtectedRoute>
              <AssignmentView />
            </ProtectedRoute>
          }
        />

        <Route
          path="/educator/lesson/:lessonId/quiz/new"
          element={
            <ProtectedRoute>
              <QuizBuilder />
            </ProtectedRoute>
          }
        />

        <Route
          path="/educator/quiz/:quizId/edit"
          element={
            <ProtectedRoute>
              <QuizBuilder />
            </ProtectedRoute>
          }
        />

        <Route
          path="/educator/lesson/:lessonId/assignment/new"
          element={
            <ProtectedRoute>
              <AssignmentBuilder />
            </ProtectedRoute>
          }
        />

        <Route
          path="/educator/assignment/:assignmentId/edit"
          element={
            <ProtectedRoute>
              <AssignmentBuilder />
            </ProtectedRoute>
          }
        />

        <Route
          path="/educator/assignment/:assignmentId/grade"
          element={
            <ProtectedRoute>
              <AssignmentGrading />
            </ProtectedRoute>
          }
        />

        <Route
          path="/courses/:id/learn"
          element={
            <DashboardLayout title="Learning" hideNavigation={true}>
              <CourseLearning />
            </DashboardLayout>
          }
        />

        <Route
          path="/my-courses"
          element={
            <DashboardLayout title="My Courses">
              <MyCourses />
            </DashboardLayout>
          }
        />

        <Route
          path="/learning/progress"
          element={
            <DashboardLayout title="Learning Progress">
              <LearningProgress />
            </DashboardLayout>
          }
        />

        {/* Dashboard Routes - with DashboardLayout */}
        <Route
          path="/dashboard/uk/pathfinder"
          element={
            <DashboardLayout title="UK Pathfinder">
              <UKPathfinder />
            </DashboardLayout>
          }
        />

        <Route
          path="/dashboard/uk/financial-analysis"
          element={
            <DashboardLayout title="Financial Analysis">
              <FinancialAnalysis />
            </DashboardLayout>
          }
        />

        <Route
          path="/dashboard/uk/readiness-assessment"
          element={
            <DashboardLayout title="Readiness Assessment">
              <UKReadinessAssessment />
            </DashboardLayout>
          }
        />

        <Route
          path="/dashboard/application"
          element={
            <DashboardLayout title="Application">
              <DocumentSubmission />
            </DashboardLayout>
          }
        />

        <Route
          path="/dashboard/progress"
          element={
            <DashboardLayout title="Progress">
              <ApplicationProgress />
            </DashboardLayout>
          }
        />

        <Route
          path="/profile"
          element={
            <DashboardLayout title="Profile">
              <Profile />
            </DashboardLayout>
          }
        />

        <Route
          path="/settings"
          element={
            <DashboardLayout title="Settings">
              <Settings />
            </DashboardLayout>
          }
        />

        <Route
          path="/my-scratch-cards"
          element={
            <DashboardLayout title="My Scratch Cards">
              <PurchasedCards />
            </DashboardLayout>
          }
        />

        {/* Checkout */}
        <Route
          path="/checkout/scratch-cards"
          element={<ScratchCardCheckout />}
        />

        {/* Legacy route - redirect to dashboard route */}
        <Route
          path="/overseas/application"
          element={
            <DashboardLayout title="Application">
              <DocumentSubmission />
            </DashboardLayout>
          }
        />

        {/* Admin Routes */}
        {/* Dashboard */}
        <Route
          path="/admin/dashboard"
          element={<AdminDashboard />}
        />

        {/* Applications (Unified) */}
        <Route
          path="/admin/applications"
          element={<Applications />}
        />

        <Route
          path="/admin/applications/:id"
          element={<ApplicationDetail />}
        />

        {/* Documents (Unified) */}
        <Route
          path="/admin/documents"
          element={<Documents />}
        />

        {/* User Management */}
        <Route path="/admin/users" element={<UsersList />} />
        <Route path="/admin/users/create" element={<UserCreate />} />
        <Route path="/admin/users/:id" element={<UserDetail />} />

        {/* Reports & Analytics */}
        <Route path="/admin/reports" element={<ReportsOverview />} />
        <Route path="/admin/reports/features" element={<ServiceAnalytics />} />
        <Route path="/admin/reports/users" element={<UserAnalytics />} />
        <Route path="/admin/reports/export" element={<DataExport />} />

        {/* Communications */}
        <Route path="/admin/communications/notifications" element={<BulkNotifications />} />
        <Route path="/admin/communications/announcements" element={<Announcements />} />

        {/* Operations */}
        <Route path="/admin/operations/batch" element={<BatchUpdates />} />

        {/* Settings */}
        <Route path="/admin/settings/system" element={<SystemConfig />} />
        <Route path="/admin/settings/features" element={<FeatureManagement />} />
        <Route path="/admin/settings/statuses" element={<StatusesConfig />} />
        <Route path="/admin/settings/documents" element={<DocumentTypesConfig />} />
        <Route path="/admin/settings/emails" element={<EmailTemplatesConfig />} />

        {/* Scratch Cards Management */}
        <Route path="/admin/scratch-cards" element={<ScratchCardsManagement />} />
        <Route path="/admin/scratch-cards/create" element={<ProductForm />} />
        <Route path="/admin/scratch-cards/edit/:id" element={<ProductForm />} />
      </Route>
    </Routes>
  );
};

// Main App Component wrapped in Auth Context and Error Boundary
function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <div className="App">
            <AppContent />
          </div>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;